import type { UserAuth } from '@repo/common';
import type { ConversationModel } from '@repo/common/types/Conversation';
import type { ExchangeMessage } from '@repo/common/types/Exchange';
import type { MessageModel } from '@repo/common/types/Message';
import type { TableName } from '@repo/common/types/SharedLib';
import type { StoredCalendarEventModel } from '@repo/common/types/StoredCalendarEvent';
import type { Task, TaskModel } from '@repo/common/types/Task';
import type { TranscriptModel } from '@repo/common/types/Transcript';
import type { WorkspaceCommonItem, WorkspaceModel } from '@repo/common/types/Workspace';
import type { AssetModel } from '@repo/common/types/Asset';
import type { AssetCommonItem } from '@repo/common/types/Adapter';
import {
  TABLE_NAMES_IN_SYNC_ORDER,
  getDependencies,
  getTableNamesInSyncOrder,
  getValidTableNames,
  hasDependencies,
  isValidTableName
} from './adapterUtils';
import { StoredCalendarEventClientAdapter } from './client/calendarEvent.client.adapter';
import { ConversationClientAdapter } from './client/conversation.client.adapter';
import { MessageClientAdapter } from './client/message.client.adapter';
import { TaskClientAdapter } from './client/task.client.adapter';
import { TranscriptClientAdapter } from './client/transcript.client.adapter';
import { WorkspaceClientAdapter } from './client/workspace.client.adapter';
import { AssetClientAdapter } from './client/asset.client.adapter';

// ─── TYPE DEFINITIONS ───

export type CommonItemType = WorkspaceCommonItem | Task | ExchangeMessage | AssetCommonItem;

export interface ClientAdapterTableConfig {
  name: TableName;
  displayName: string;
  description: string;
  syncOrder: number; // Lower numbers sync first (dependencies)
  dependencies: TableName[]; // Tables this table depends on
  clientAdapter: {
    class: any; // Flexible constructor type
    modelGetter: (models: {
      workspaceModel: WorkspaceModel;
      taskModel: TaskModel;
      transcriptModel: TranscriptModel;
      messageModel: MessageModel;
      conversationModel: ConversationModel;
      calendarEventModel: StoredCalendarEventModel;
      assetModel: AssetModel;
    }) =>
      | WorkspaceModel
      | TaskModel
      | TranscriptModel
      | MessageModel
      | ConversationModel
      | StoredCalendarEventModel
      | AssetModel;
  };
}

// ─── CLIENT ADAPTER TABLE CONFIGURATIONS ───

export const CLIENT_ADAPTER_TABLES: Record<TableName, ClientAdapterTableConfig> = {
  workspace: {
    name: 'workspace',
    displayName: 'Workspace',
    description: 'User workspaces for organizing content',
    syncOrder: 1,
    dependencies: [],
    clientAdapter: {
      class: WorkspaceClientAdapter,
      modelGetter: (models: any) => models.workspaceModel
    }
  },
  transcript: {
    name: 'transcript',
    displayName: 'Transcript',
    description: 'Audio transcriptions and recordings',
    syncOrder: 2,
    dependencies: ['workspace'],
    clientAdapter: {
      class: TranscriptClientAdapter,
      modelGetter: (models: any) => models.transcriptModel
    }
  },
  asset: {
    name: 'asset',
    displayName: 'Asset',
    description: 'User-authored assets (documents, drafts, notes)',
    syncOrder: 3,
    dependencies: ['workspace'],
    clientAdapter: {
      class: AssetClientAdapter,
      modelGetter: (models: any) => models.assetModel
    }
  },
  task: {
    name: 'task',
    displayName: 'Task',
    description: 'User tasks and to-dos',
    syncOrder: 4,
    dependencies: ['workspace'],
    clientAdapter: {
      class: TaskClientAdapter,
      modelGetter: (models: any) => models.taskModel
    }
  },
  conversation: {
    name: 'conversation',
    displayName: 'Conversation',
    description: 'Chat conversations and threads',
    syncOrder: 5,
    dependencies: ['transcript'],
    clientAdapter: {
      class: ConversationClientAdapter,
      modelGetter: (models: any) => models.conversationModel
    }
  },
  message: {
    name: 'message',
    displayName: 'Message',
    description: 'Individual messages in conversations',
    syncOrder: 6,
    dependencies: ['conversation'],
    clientAdapter: {
      class: MessageClientAdapter,
      modelGetter: (models: any) => models.messageModel
    }
  },
  calendarEvent: {
    name: 'calendarEvent',
    displayName: 'Calendar Event',
    description: 'Calendar events and meetings',
    syncOrder: 7,
    dependencies: ['workspace'],
    clientAdapter: {
      class: StoredCalendarEventClientAdapter,
      modelGetter: (models: any) => models.calendarEventModel
    }
  }
} as unknown as Record<TableName, ClientAdapterTableConfig>;

// ─── RE-EXPORT UTILITY FUNCTIONS ───

export {
  TABLE_NAMES_IN_SYNC_ORDER,
  getTableNamesInSyncOrder,
  getValidTableNames,
  isValidTableName
};

// ─── CLIENT-SPECIFIC UTILITY FUNCTIONS ───

/**
 * Get table configuration by name
 */
export function getTableConfig(tableName: string): ClientAdapterTableConfig | null {
  return CLIENT_ADAPTER_TABLES[tableName as TableName] || null;
}

/**
 * Check if a table has dependencies
 */
export function hasTableDependencies(tableName: TableName): boolean {
  return hasDependencies(tableName, CLIENT_ADAPTER_TABLES);
}

/**
 * Get dependencies for a table
 */
export function getTableDependencies(tableName: TableName): TableName[] {
  return getDependencies(tableName, CLIENT_ADAPTER_TABLES);
}

/**
 * Create client adapter instance
 */
export function createClientAdapter(
  tableName: TableName,
  models: {
    workspaceModel: WorkspaceModel;
    taskModel: TaskModel;
    transcriptModel: TranscriptModel;
    messageModel: MessageModel;
    conversationModel: ConversationModel;
    calendarEventModel: StoredCalendarEventModel;
    assetModel: AssetModel;
  },
  userId: string,
  authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  adapterConfig?: Partial<{
    baseUrl: string;
    authToken: string;
    timeout: number;
    retries: number;
    batchSize: number;
  }>
) {
  const config = CLIENT_ADAPTER_TABLES[tableName];
  if (!config) {
    throw new Error(`Table ${tableName} not found in CLIENT_ADAPTER_TABLES`);
  }
  const model = config.clientAdapter.modelGetter(models);
  return new config.clientAdapter.class(model, userId, authToken, adapterConfig);
}
